import { Injectable } from '@tanbo/vue-di-plugin'
import { Http } from '@/services/http'
import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import { Subject } from 'rxjs'
import { ParamsClean } from '@/common/decorators/params-clean'
import { AuthenticList, AuthenticRequestParams, SoftmgrAuthenticSoftStatService } from '../../service'
import { formatTimeStamp } from '@/utils/time'
import { getSoftGroupCatalogListSync } from '@/utils/soft-group-catalog'

export enum RankType {
  duration = 1,
  useSoftCount = 2,
  terminalCount = 3
}

export const rankCardTitleI18nKey: Record<RankType, string> = {
  [RankType.duration]: 'genuineSoftwareUsage.rankAuthSoftDurationDist',
  [RankType.useSoftCount]: 'genuineSoftwareUsage.rankAuthSoftUseCountDist',
  [RankType.terminalCount]: 'genuineSoftwareUsage.rankAuthSoftTerminalCountDist'
}

/** 软件视图排行饼图 series.name */
export const rankCardSeriesNameI18nKey: Record<RankType, string> = {
  [RankType.duration]: 'genuineSoftwareUsage.rankSeriesSoftDuration',
  [RankType.useSoftCount]: 'genuineSoftwareUsage.rankSeriesSoftUseCount',
  [RankType.terminalCount]: 'genuineSoftwareUsage.rankSeriesSoftTerminalCount'
}

/** 表格行：与 `/softmgr/authentic-soft/list`（AuthenticList）字段一致，时间已为展示字符串 */
export interface AuthenticSoftListRow {
  logo_url: string
  soft_name: string
  auth_status: number
  auth_count: number
  install_count: number
  avg_use_count: number
  avg_duration: number
  soft_hash_key: string
  soft_id: number
  soft_version: string
  install_time: string
  last_active_time: string
}

export interface SoftwareUsageRes {
  list: AuthenticSoftListRow[]
  total: number
}

export interface SoftGroup {
  group_id: number
  group_name: string
  group_desc: string
}

/** 列表接口行 → 表格行 */
export function mapAuthenticListToTableRow (item: AuthenticList, index: number): AuthenticSoftListRow {
  const installTs = item.install_time
  const lastTs = item.last_active_time
  const sid = item.soft_id ?? index
  return {
    logo_url: item.logo_url ?? '',
    soft_name: item.soft_name,
    auth_status: item.auth_status,
    auth_count: item.auth_count ?? 0,
    install_count: item.install_count ?? 0,
    avg_use_count: item.avg_use_count ?? 0,
    avg_duration: item.avg_duration ?? 0,
    soft_hash_key: item.soft_hash_key ?? `__soft_id_${sid}`,
    soft_id: sid,
    soft_version: item.soft_version ?? '',
    install_time: installTs != null ? formatTimeStamp(String(installTs), 'YYYY-MM-DD HH:mm:ss') : '',
    last_active_time: lastTs != null ? formatTimeStamp(String(lastTs), 'YYYY-MM-DD HH:mm:ss') : ''
  }
}

@Injectable()
export class SoftwareViewService {
  updateEvent = new Subject()

  constructor (
    private http: Http,
    private authenticSoftStat: SoftmgrAuthenticSoftStatService
  ) {
  }

  @InvokeThrottle()
  @ParamsClean()
  getAuthenticSoftList (data: AuthenticRequestParams) {
    // 0 为合法筛选项（未授权），不可用 ||=；仅 null/undefined 表示不按授权状态筛，传 -1
    data.auth_status ??= -1
    return this.authenticSoftStat.getSoftmgrAuthenticSoftList(data)
      .then(res => ({
        list: (res.list || []).map((row, i) => mapAuthenticListToTableRow(row, i)),
        total: res.total ?? 0
      }))
  }

  @ParamsClean()
  download (type: 0|1, options: AuthenticRequestParams, moduleName: string) {
    const params = Object.assign({}, options, {
      file_type: type,
      page: 0,
      limit: 0
    })
    return this.http.downloadWithBlob('/softmgr/authentic-soft/list/export', params, moduleName)
  }

  softGroup () {
    return Promise.resolve({ list: getSoftGroupCatalogListSync() })
  }
}
